/**
 * Tells us, from real readers, how Back went: how long it took to put the
 * listing page back, and how far from where they left it they landed.
 *
 * On 2026-10-26 Back to home 12 screens down showed the product page for 6.6s,
 * and an earlier fix had landed readers at the footer. Nothing reported either:
 * no error throws when a page is slow or in the wrong place. A reader just sees
 * it. This sends one `back_restore` event per Back, and the frontend-health
 * cron (pages/api/cron/frontend-health.ts) checks them every 15 minutes.
 *
 * Called by utils/useScrollRestoration.tsx, the one place every listing page
 * restores its scroll, once the page is back or the restore gave up.
 */
import { getAnalytics } from './analytics';
import { pageEnteredByBackAt } from './navigation-direction';
import { anchorDrift, type ScrollAnchor } from './scroll-anchor';
import { BACK_RESTORE_EVENT, backKind, deviceClass, type BackOutcome, type BackWait } from './back-restore-kinds';

export { BACK_RESTORE_EVENT };

/** Drift is read this long after the page is back, once ads and pills have loaded. */
export const SETTLE_MS = 1500;

/** The reader taking over: drift after this is their scroll, not ours. */
const READER_INPUT = ['touchstart', 'wheel', 'keydown', 'mousedown'] as const;

/** The Back visit last reported, so a page restored twice reports once. */
let reportedVisit: number | null = null;

/** Test seam. */
export function resetBackRestoreReport(): void {
  reportedVisit = null;
  routeWatch = null;
}

/** When the scroll restore began waiting for the page, and what it waited on last. */
export interface BackRestoreWait {
  /** `performance.now()` when the route had changed and the restore began checking. */
  startedAt: number;
  waitedOn: BackWait;
  /**
   * What the page looked like at that last check, so a wait on `height` or
   * `count` says why. Prod 2026-10-27: two search Backs waited 4.6s and 5.6s
   * on height (one gave up 31065px short of the reader) and an `other` Back
   * spent 6.8s with nothing waited on, and no row could say why.
   */
  page?: BackRestorePage;
  /**
   * `performance.now()` when a zero-delay timer set at `startedAt` ran. Late
   * along with the first look: a script held the main thread. On time while
   * the first look was late: the browser did not draw a frame. Prod
   * 2026-10-29: first looks of 1.2-4s with the tab visible (search phones p75
   * 3973ms, home desktop with 270 tiles 1846ms) and nothing to tell which.
   */
  firstTickAt?: number;
  /** Long frames or tasks seen while Back ran (`watchMainThread`). */
  mainThread?: MainThreadWatch;
}

/** What the route change fetched between Back and the route changing. */
export interface RouteFetch {
  /** Back pressed -> the last of those fetches landed, capped at the route change. */
  fetchMs: number;
  /** What that last fetch was: `data` (page props), `chunk` (page code) or `other` of ours. */
  kind: 'data' | 'chunk' | 'other';
  /** That fetch came from the browser's cache: nothing crossed the network. */
  cached: boolean;
}

export interface RouteFetchWatch {
  /** Our own fetches that started from `from` to `to` (`performance.now()` times). Stops the watch. */
  read: (from: number, to: number) => RouteFetch | null;
}

/** The parts of a resource timing entry read here. */
interface ResourceEntry {
  name: string;
  startTime: number;
  responseEnd: number;
  transferSize?: number;
}

function routeFetchKind(pathname: string): RouteFetch['kind'] {
  if (pathname.startsWith('/_next/data/')) return 'data';
  if (pathname.startsWith('/_next/static/')) return 'chunk';
  return 'other';
}

/**
 * Start watching the fetches our own site makes. Started on popstate, before
 * the router asks for anything, because the browser's resource buffer holds
 * 250 entries and a reader who scrolled a few pages of tiles has filled it.
 */
export function watchRouteFetches(): RouteFetchWatch {
  const entries: ResourceEntry[] = [];
  const supported: readonly string[] =
    typeof PerformanceObserver === 'undefined' ? [] : (PerformanceObserver.supportedEntryTypes ?? []);
  let observer: PerformanceObserver | null = null;
  if (supported.includes('resource')) {
    try {
      observer = new PerformanceObserver((list) => {
        entries.push(...(list.getEntries() as unknown as ResourceEntry[]));
      });
      observer.observe({ type: 'resource' });
    } catch (_e) {
      observer = null;
    }
  }
  return {
    read(from, to) {
      if (observer) {
        entries.push(...(observer.takeRecords() as unknown as ResourceEntry[]));
        observer.disconnect();
        observer = null;
      }
      let last: { entry: ResourceEntry; kind: RouteFetch['kind']; end: number } | null = null;
      for (const entry of entries) {
        if (entry.startTime < from || entry.startTime > to) continue;
        let url: URL;
        try {
          url = new URL(entry.name, window.location.href);
        } catch (_e) {
          continue;
        }
        if (url.origin !== window.location.origin) continue;
        const end = Math.min(entry.responseEnd || to, to);
        if (!last || end > last.end) last = { entry, kind: routeFetchKind(url.pathname), end };
      }
      if (!last) return null;
      return {
        fetchMs: Math.max(0, Math.round(last.end - from)),
        kind: last.kind,
        cached: last.entry.transferSize === 0,
      };
    },
  };
}

/**
 * The route watch for the Back in flight. Prod 2026-10-30, after the busy
 * fields landed: the route change, not the page filling, was most of a slow
 * Back (search route p75 1557ms) and the long frames in the whole Back only
 * ~500ms. The rest is either a fetch the router waited on or work too short
 * to count as a long frame, and nothing said which.
 */
let routeWatch: { at: number; watch: RouteFetchWatch } | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    routeWatch?.watch.read(0, 0);
    routeWatch = { at: performance.now(), watch: watchRouteFetches() };
  });
}

/** The busy main thread between Back and the first look. */
export interface MainThreadBusy {
  /** Which browser API saw it: `none` where neither exists (Safari, Firefox), so a missing `busy_ms` is not a zero. */
  api: 'loaf' | 'longtask' | 'none';
  /** Milliseconds of long frames or tasks inside the span. */
  busyMs: number;
  /** The part of `busyMs` before `routeEnd`, when `read` was given one: the route change's share. */
  routeMs?: number;
  /** Where the longest script in those frames came from: `self`, a host, or `unknown`. Long frames only. */
  by: string | null;
  /**
   * The file of that script (`search-6f2a.js`), what started it
   * (`MessagePort.onmessage` is React's scheduler, `Response.json.then` a
   * fetch landing) and the ms those frames spent on style and layout rather
   * than script. Long frames only. Prod 2026-10-30: search and browse Backs
   * held the main thread 3.5-7.5s, all `self`, and nothing said which work.
   */
  script?: string;
  invoker?: string;
  layoutMs?: number;
}

export interface MainThreadWatch {
  api: MainThreadBusy['api'];
  /**
   * How busy the main thread was from `from` to `to` (`performance.now()`
   * times), and up to `routeEnd` when given. Stops the watch.
   */
  read: (from: number, to: number, routeEnd?: number) => MainThreadBusy;
}

/** The parts of a long-animation-frame script entry read here; not in every TS lib yet. */
interface LoafScript {
  duration: number;
  sourceURL?: string;
  invoker?: string;
  invokerType?: string;
}

/** The file name of a script, cleaned for the track schema ([a-z0-9._-]). */
function scriptFile(sourceURL: string | undefined): string | undefined {
  if (!sourceURL) return undefined;
  try {
    const { pathname } = new URL(sourceURL, window.location.href);
    const file = pathname.slice(pathname.lastIndexOf('/') + 1);
    return file.toLowerCase().replace(/[^a-z0-9._-]/g, '-').slice(0, 80) || undefined;
  } catch (_e) {
    return undefined;
  }
}

/**
 * What started the script. A script tag's invoker is its URL, already sent as
 * the file, so those send their type; callbacks and promises send the name.
 */
function scriptInvoker(script: LoafScript): string | undefined {
  const named = script.invokerType !== 'classic-script' && script.invokerType !== 'module-script';
  const raw = named && script.invoker ? script.invoker : script.invokerType;
  return raw?.replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 80) || undefined;
}

function scriptHost(sourceURL: string | undefined): string {
  if (!sourceURL) return 'unknown';
  try {
    const url = new URL(sourceURL, window.location.href);
    if (url.origin === window.location.origin) return 'self';
    // The track schema takes [a-z0-9.-] only, and one bad value refuses the whole batch.
    return url.hostname.toLowerCase().replace(/[^a-z0-9.-]/g, '-').slice(0, 80) || 'unknown';
  } catch (_e) {
    return 'unknown';
  }
}

/**
 * Start watching for long frames (Chrome's long-animation-frame, which names
 * the scripts) or, failing that, long tasks. Buffered, so frames from just
 * before the watch began count too.
 */
export function watchMainThread(): MainThreadWatch {
  const entries: PerformanceEntry[] = [];
  const supported: readonly string[] =
    typeof PerformanceObserver === 'undefined' ? [] : (PerformanceObserver.supportedEntryTypes ?? []);
  const type = supported.includes('long-animation-frame') ? 'long-animation-frame' : supported.includes('longtask') ? 'longtask' : null;
  let observer: PerformanceObserver | null = null;
  if (type) {
    try {
      observer = new PerformanceObserver((list) => {
        entries.push(...list.getEntries());
      });
      observer.observe({ type, buffered: true });
    } catch (_e) {
      observer = null;
    }
  }
  const api: MainThreadBusy['api'] = !observer ? 'none' : type === 'longtask' ? 'longtask' : 'loaf';

  return {
    api,
    read(from, to, routeEnd) {
      if (observer) {
        entries.push(...observer.takeRecords());
        observer.disconnect();
        observer = null;
      }
      let busyMs = 0;
      let routeMs = 0;
      let layoutMs = 0;
      let longest: LoafScript | null = null;
      for (const entry of entries) {
        const end = entry.startTime + entry.duration;
        const overlap = Math.min(end, to) - Math.max(entry.startTime, from);
        if (overlap <= 0) continue;
        busyMs += overlap;
        if (routeEnd !== undefined) routeMs += Math.max(0, Math.min(end, to, routeEnd) - Math.max(entry.startTime, from));
        const frame = entry as PerformanceEntry & { scripts?: LoafScript[]; styleAndLayoutStart?: number };
        if (frame.styleAndLayoutStart) layoutMs += Math.max(0, Math.min(end, to) - Math.max(frame.styleAndLayoutStart, from));
        for (const script of frame.scripts ?? []) if (!longest || script.duration > longest.duration) longest = script;
      }
      const route = routeEnd === undefined ? {} : { routeMs: Math.round(routeMs) };
      if (api !== 'loaf') return { api, busyMs: Math.round(busyMs), ...route, by: null };
      return {
        api,
        busyMs: Math.round(busyMs),
        ...route,
        by: longest ? scriptHost(longest.sourceURL) : null,
        script: longest ? scriptFile(longest.sourceURL) : undefined,
        invoker: longest ? scriptInvoker(longest) : undefined,
        layoutMs: Math.round(layoutMs),
      };
    },
  };
}

/** The page at the restore's last check. */
export interface BackRestorePage {
  /** `performance.now()` of the first check: a late one means the tab was hidden or the page was busy. */
  firstLookAt: number;
  /** The page was hidden at some check: a background tab does not run the checks. */
  hidden: boolean;
  /** Real tiles in the grid. */
  tiles: number;
  /** Skeleton tiles holding the page's height (components/search-listing/useGridHoldOnBack.ts). */
  holdTiles: number;
  /** Pages the reader had loaded when they left, from storage; 0 when none was saved. */
  savedPages: number;
  /** How far the page was from tall enough to scroll to where they were; 0 when it was. */
  shortPx: number;
}

function pageProperties(wait: BackRestoreWait): Record<string, string> {
  const page = wait.page;
  if (!page) return {};
  return {
    first_look_ms: String(Math.max(0, Math.round(page.firstLookAt - wait.startedAt))),
    hidden: page.hidden ? '1' : '0',
    tiles: String(page.tiles),
    hold_tiles: String(page.holdTiles),
    saved_pages: String(page.savedPages),
    short_px: String(Math.max(0, Math.round(page.shortPx))),
  };
}

/**
 * Was the main thread busy before the first look? Read once the page has
 * settled: a long frame is only reported after it ends, and the one that held
 * the first look ends with it.
 */
function busyProperties(wait: BackRestoreWait, visit: number): Record<string, string> {
  const out: Record<string, string> = {};
  if (wait.firstTickAt !== undefined) {
    out.first_tick_ms = String(Math.max(0, Math.round(wait.firstTickAt - wait.startedAt)));
  }
  if (wait.mainThread) {
    const busy = wait.mainThread.read(visit, wait.page?.firstLookAt ?? wait.startedAt, wait.startedAt);
    out.busy_api = busy.api;
    if (busy.api !== 'none') out.busy_ms = String(busy.busyMs);
    if (busy.api !== 'none' && busy.routeMs !== undefined) out.route_busy_ms = String(busy.routeMs);
    if (busy.by) out.busy_by = busy.by;
    if (busy.script) out.busy_script = busy.script;
    if (busy.invoker) out.busy_invoker = busy.invoker;
    if (busy.layoutMs !== undefined) out.busy_layout_ms = String(busy.layoutMs);
  }
  return out;
}

/** What the route change waited on the network for, when the watch saw it start. */
function routeFetchProperties(wait: BackRestoreWait, visit: number): Record<string, string> {
  // The popstate navigation-direction timed, not one from an older Back.
  if (!routeWatch || Math.abs(routeWatch.at - visit) > 50) return {};
  const fetched = routeWatch.watch.read(visit, wait.startedAt);
  routeWatch = null;
  if (!fetched) return { route_fetch: 'none' };
  return {
    route_fetch: fetched.kind,
    route_fetch_ms: String(fetched.fetchMs),
    route_fetch_cached: fetched.cached ? '1' : '0',
  };
}

/**
 * Report this Back once the page is back (`restored`) or the restore gave up
 * and went to the top (`gave_up`). Does nothing on a page not entered by Back.
 *
 * Drift is where the anchor listing sits against where the reader left it.
 * With no anchor on the page it is the scroll position against the saved one.
 * It is left out when the reader scrolled or touched before it was read.
 *
 * `wait` splits shown_ms in two. Readers' home Backs ran 4-7s p75 on phones
 * from the first hour this reported (2026-10-27, clusters 895a2080416e108e,
 * 7f408b8c06497576), after every Back fix had landed, and one number could not
 * say whether the route change was slow or the page took that long to fill.
 */
export function reportBackRestore(params: {
  outcome: BackOutcome;
  savedY: number;
  anchor: ScrollAnchor | null;
  wait?: BackRestoreWait;
}): void {
  if (typeof window === 'undefined') return;
  const visit = pageEnteredByBackAt();
  if (visit === null || visit === reportedVisit) return;
  reportedVisit = visit;

  const now = performance.now();
  const shownMs = Math.max(0, Math.round(now - visit));
  const split = params.wait
    ? {
        route_ms: String(Math.max(0, Math.round(params.wait.startedAt - visit))),
        wait_ms: String(Math.max(0, Math.round(now - params.wait.startedAt))),
        waited_on: params.wait.waitedOn,
        ...pageProperties(params.wait),
      }
    : {};
  const kind = backKind(window.location.pathname);
  const view = document.querySelector('[data-testid="showcase-view"]') ? 'showcase' : 'grid';
  const device = deviceClass(window.innerWidth);

  let moved = false;
  const onInput = () => {
    moved = true;
  };
  for (const type of READER_INPUT) window.addEventListener(type, onInput, { capture: true, passive: true });

  window.setTimeout(() => {
    for (const type of READER_INPUT) window.removeEventListener(type, onInput, true);
    // Gone to another page before it settled: nothing left to measure.
    if (pageEnteredByBackAt() !== visit) moved = true;

    let drift: number | null = null;
    if (!moved) {
      drift = params.anchor ? anchorDrift(params.anchor) : null;
      if (drift === null) drift = window.scrollY - params.savedY;
    }

    getAnalytics()?.track({
      event_type: BACK_RESTORE_EVENT,
      properties: {
        kind,
        view,
        outcome: params.outcome,
        device,
        shown_ms: String(shownMs),
        ...split,
        reader_moved: moved ? '1' : '0',
        ...(drift === null ? {} : { drift_px: String(Math.round(drift)) }),
        ...(params.wait ? busyProperties(params.wait, visit) : {}),
        ...(params.wait ? routeFetchProperties(params.wait, visit) : {}),
      },
    });
  }, SETTLE_MS);
}
